/**
 * Floating3DCard - Premium 3D floating card with perspective effects
 *
 * Creates an immersive card that appears to float in 3D space,
 * entering from depth with smooth parallax and tilt effects.
 *
 * Features:
 * - Mouse-follow tilt (configurable maxTilt, default 8 degrees)
 * - 5 entry directions: bottom, left, right, top, depth
 * - Floating animation (y: 0 -> -8 -> 0 over 6s)
 * - Glow on hover
 * - Shine/reflection sweep effect
 */

import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { forwardRef, useRef, useState } from "react";
import { cn } from "../utils";

export type EntryDirection = "bottom" | "left" | "right" | "top" | "depth";

export interface Floating3DCardProps {
  children: React.ReactNode;
  /** Delay before card animates in (seconds) */
  delay?: number;
  /** Direction card enters from */
  enterFrom?: EntryDirection;
  /** Enable mouse-follow tilt effect */
  enableTilt?: boolean;
  /** Maximum tilt angle in degrees */
  maxTilt?: number;
  /** Card glow color */
  glowColor?: string;
  /** Enable floating animation */
  enableFloat?: boolean;
  /** Additional className */
  className?: string;
  /** Z-index for layering */
  zIndex?: number;
  /** Whether to use dark mode styling */
  dark?: boolean;
}

export const Floating3DCard = forwardRef<HTMLDivElement, Floating3DCardProps>(
  function Floating3DCard(
    {
      children,
      delay = 0,
      enterFrom = "depth",
      enableTilt = true,
      maxTilt = 8,
      glowColor = "#8b5cf6",
      enableFloat = true,
      className = "",
      zIndex = 1,
      dark,
    },
    ref
  ) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    // Detect dark mode if not explicitly set
    const isDark =
      dark ??
      (typeof window !== "undefined" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    // Mouse position for tilt effect
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    // Spring physics for smooth tilt
    const springConfig = { damping: 20, stiffness: 300 };
    const rotateX = useSpring(
      useTransform(mouseY, [-0.5, 0.5], [maxTilt, -maxTilt]),
      springConfig
    );
    const rotateY = useSpring(
      useTransform(mouseX, [-0.5, 0.5], [-maxTilt, maxTilt]),
      springConfig
    );

    // Handle mouse move for tilt
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!enableTilt || !cardRef.current) return;

      const rect = cardRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      mouseX.set((e.clientX - centerX) / rect.width);
      mouseY.set((e.clientY - centerY) / rect.height);
    };

    const handleMouseLeave = () => {
      mouseX.set(0);
      mouseY.set(0);
      setIsHovered(false);
    };

    // Entry animation variants based on direction
    const getEntryVariants = () => {
      const base = { opacity: 0 };
      const visible = {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        rotateX: 0,
        rotateY: 0,
      };

      switch (enterFrom) {
        case "bottom":
          return { hidden: { ...base, y: 100, scale: 0.9 }, visible };
        case "left":
          return {
            hidden: { ...base, x: -100, rotateY: 15, scale: 0.9 },
            visible,
          };
        case "right":
          return {
            hidden: { ...base, x: 100, rotateY: -15, scale: 0.9 },
            visible,
          };
        case "top":
          return { hidden: { ...base, y: -100, scale: 0.9 }, visible };
        case "depth":
        default:
          return {
            hidden: { ...base, scale: 0.8, rotateX: 10 },
            visible,
          };
      }
    };

    const variants = getEntryVariants();

    return (
      <motion.div
        ref={ref || cardRef}
        className={cn("relative", className)}
        style={{
          perspective: "1200px",
          perspectiveOrigin: "center",
          zIndex,
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={variants}
        transition={{
          duration: 0.8,
          delay,
          ease: [0.25, 0.1, 0.25, 1],
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
      >
        <motion.div
          ref={cardRef}
          className="relative w-full h-full rounded-2xl overflow-hidden"
          style={{
            rotateX: enableTilt ? rotateX : 0,
            rotateY: enableTilt ? rotateY : 0,
            transformStyle: "preserve-3d",
            background: isDark
              ? "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)"
              : "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: `1px solid ${
              isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
            }`,
            boxShadow: isHovered
              ? `0 25px 50px -12px ${glowColor}30, 0 0 0 1px ${glowColor}20, inset 0 1px 0 ${
                  isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.5)"
                }`
              : `0 20px 40px -15px rgba(0,0,0,${
                  isDark ? "0.4" : "0.15"
                }), inset 0 1px 0 ${
                  isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.5)"
                }`,
            transition: "box-shadow 0.3s ease",
          }}
          animate={
            enableFloat
              ? {
                  y: [0, -8, 0],
                }
              : {}
          }
          transition={
            enableFloat
              ? {
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }
              : {}
          }
        >
          {/* Gradient border glow on hover */}
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background: `linear-gradient(135deg, ${glowColor}20 0%, transparent 50%, ${glowColor}10 100%)`,
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />

          {/* Inner highlight */}
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{
              background: `linear-gradient(90deg, transparent, ${
                isDark ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.8)"
              }, transparent)`,
            }}
          />

          {/* Content */}
          <div className="relative z-10 p-8">{children}</div>

          {/* Reflection/shine effect */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.03) 45%, transparent 50%)",
              transform: isHovered ? "translateX(100%)" : "translateX(-100%)",
              transition: "transform 0.6s ease",
            }}
          />
        </motion.div>
      </motion.div>
    );
  }
);

Floating3DCard.displayName = "Floating3DCard";
